import { ArrowLeft, FileText, Globe2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { strings, useLanguage } from '@/lib/strings';
import { cn } from '@/lib/utils';

type LegalCopy = {
  badge: string;
  title: string;
  subtitle: string;
  updatedLabel: string;
  updatedValue: string;
  backToCatalog: string;
  sections: readonly {
    title: string;
    body: readonly string[];
  }[];
};

function languageButtonClass(active: boolean) {
  return active
    ? 'bg-foreground text-background shadow-sm'
    : 'text-muted-foreground hover:bg-[hsl(var(--surface-soft))] hover:text-foreground';
}

function renderParagraph(paragraph: string) {
  const supportLink = 't.me/dpf_adm';
  const [prefix, suffix] = paragraph.split(supportLink);

  if (suffix === undefined) {
    return paragraph;
  }

  return (
    <>
      {prefix}
      <a href="https://t.me/dpf_adm" target="_blank" rel="noreferrer" className="font-medium text-foreground underline-offset-4 hover:underline">
        {supportLink}
      </a>
      {suffix}
    </>
  );
}

function LegalPage({ copy }: { copy: LegalCopy }) {
  const { language, setLanguage } = useLanguage();

  return (
    <main className="min-h-screen px-4 py-5 md:px-6 md:py-8">
      <div className="mx-auto w-full max-w-4xl">
        <article className="surface-panel overflow-hidden p-5 md:p-8">
          <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--surface-soft))] px-3 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                <FileText className="h-3.5 w-3.5" />
                {copy.badge}
              </div>
              <h1 className="editorial-display mt-5 text-4xl font-semibold tracking-tight text-foreground md:text-6xl">
                {copy.title}
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-7 text-muted-foreground md:text-lg">
                {copy.subtitle}
              </p>
            </div>

            <div className="shrink-0 rounded-[1.35rem] border border-border/70 bg-white/55 p-2 dark:bg-white/5">
              <div className="flex items-center gap-2 px-2 pb-2 text-xs font-semibold text-muted-foreground">
                <Globe2 className="h-3.5 w-3.5" />
                {strings.topbar.language}
              </div>
              <div className="grid grid-cols-2 gap-1" role="group" aria-label={strings.topbar.language}>
                {(['en', 'ru'] as const).map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLanguage(code)}
                    className={cn('rounded-full px-4 py-2 text-sm font-semibold transition', languageButtonClass(language === code))}
                    aria-pressed={language === code}
                  >
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 border-t border-border/70 pt-5 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <p>
              {copy.updatedLabel}: {copy.updatedValue}
            </p>
            <Link to="/catalog" className="secondary-pill-button rounded-full">
              <ArrowLeft className="h-4 w-4" />
              {copy.backToCatalog}
            </Link>
          </div>

          <section className="mt-6 border-t border-border/70 pt-2">
            {copy.sections.map((section, index) => (
              <section key={section.title} className="border-b border-border/70 py-5 last:border-b-0 last:pb-0">
                <h2 className="text-xl font-semibold tracking-tight text-foreground md:text-2xl">
                  {index + 1}. {section.title}
                </h2>
                <div className="mt-3 grid gap-3 text-sm leading-7 text-muted-foreground md:text-base md:leading-8">
                  {section.body.map((paragraph) => (
                    <p key={paragraph}>{renderParagraph(paragraph)}</p>
                  ))}
                </div>
              </section>
            ))}
          </section>
        </article>
      </div>
    </main>
  );
}

export function PolicyPage() {
  return <LegalPage copy={strings.policy} />;
}

export function TermsPage() {
  return <LegalPage copy={strings.terms} />;
}
